import React, { useEffect, useRef } from "react";
import { FormattedMessage } from "react-intl";
import { getYahooAdsUri } from "./yahooFeed";
import { toTitleCase } from "../commons/HelperFunctions";
import { llmTermTitleCase } from "../commons/llmKeywords";
import { useLanderConfig } from "../commons/ConfigContext";
import { sendYahooEvent, EventType } from "./yahooEvent";
import { RSItem, TermOrigin } from "./types";
import { trackImpression, trackClick } from "../commons/signals";
import SearchIcon from "@ux/icon/search";
import ChevronRightIcon from "@ux/icon/chevron-right";
import "@ux/icon/search/index.css";
import "@ux/icon/chevron-right/index.css";

interface YahooRSProps {
  items: RSItem[];
  maxRS?: number;
  headerMessageId?: string;
  /**
   * PARKING-5307: the partner terms behind this block's PARTNER rows came from
   * the API's LLM keywords. Left undefined, the block reports nothing about LLM
   * keywords and every row is title-cased as before.
   */
  partnerTermsFromLlm?: boolean;
}

export const YahooRS: React.FC<YahooRSProps> = ({
  items,
  maxRS,
  headerMessageId,
  partnerTermsFromLlm,
}) => {
  const config = useLanderConfig();
  const rootDomain = config.landerConfig.domain.rootDomain;
  const displayItems = maxRS ? items.slice(0, maxRS) : items;
  const hasEventFired = useRef(false);
  const llmLanguage = config.landerConfig.keywords?.language;

  const isLlmRow = (item: RSItem) =>
    !!partnerTermsFromLlm && item.origin === TermOrigin.PARTNER;
  // toTitleCase is ASCII-only; LLM rows get a Unicode-aware version instead.
  const labelFor = (item: RSItem) =>
    isLlmRow(item)
      ? llmTermTitleCase(item.term, llmLanguage)
      : toTitleCase(item.term);
  // Only report LLM usage when the parent said whether the terms are LLM's.
  // True means the LLM terms were SHOWN: a row of theirs is on screen.
  const llmUsage = (shown: boolean) =>
    typeof partnerTermsFromLlm === "boolean"
      ? { llmKeywordsUsed: shown }
      : {};

  useEffect(() => {
    if (displayItems.length > 0 && !hasEventFired.current) {
      hasEventFired.current = true;
      const rsTerms = displayItems.map(labelFor).join(",");
      // Positionally aligned with rsTerms, so reporting can attribute an
      // impression to the source that supplied that row (PARKING-5269).
      // All-or-nothing: a partly-stamped block would produce a list that no
      // longer lines up with rsTerms, which is worse than sending none.
      const rsOrigins = displayItems.every((item) => item.origin)
        ? displayItems.map((item) => item.origin).join(",")
        : "";
      sendYahooEvent(EventType.RS_LOAD, config.landerConfig, {
        rsTerms,
        ...(rsOrigins && { rsOrigins }),
        ...llmUsage(displayItems.some(isLlmRow)),
      });
      trackImpression({ section: "related_search", widget: "rs_block" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.landerConfig, displayItems, partnerTermsFromLlm]);

  const handleClick = (keyword: string, item: RSItem) => {
    trackClick({ section: "related_search", widget: "rs_link" });
    sendYahooEvent(EventType.KEYWORD_CLICK, config.landerConfig, {
      searchQuery: keyword,
      ...(item.origin && { rsOrigin: item.origin }),
      ...llmUsage(isLlmRow(item)),
    });
  };

  return (
    <div className="yahooRS">
      <div className="relatedTopicsHeader">
        <span>
          <FormattedMessage
            id={ headerMessageId || "Yahoo-RelatedSearchTopics" }
          />
        </span>
      </div>
      <ul>
        {displayItems.map((item, index) => {
          const titleCased = labelFor(item);
          return (
            <li key={ `yahoo-rs-item-${index}` }>
              <div className="iconContainer">
                <SearchIcon style={{ color: "black" }} height="32" width="32" />
              </div>
              <a
                href={ getYahooAdsUri({
                  qs: item.qs,
                  rootDomain,
                  searchQuery: item.searchQuery,
                  system: config.landerConfig.system,
                  customerId: config.landerConfig.customerId,
                  typeTag: config.landerConfig.yahoo?.typeTag,
                }) }
                onClick={ () => handleClick(titleCased, item) }
              >
                {titleCased}
              </a>
              <div style={{ minWidth: "32px" }}>
                <ChevronRightIcon
                  style={{ color: "black" }}
                  height="32"
                  width="32"
                />
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
